import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Loader2 } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome to PodiumOne");
        navigate("/dashboard");
      } else {
        await register(email, password, name);
        navigate("/check-email");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Return to the app root (in production this resolves to https://podiumone.to)
    // so Google login lands the user on the homepage, authenticated.
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="glass-strong rounded-3xl p-8 w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-[0_0_20px_rgba(59,130,246,0.4)]">
            <Trophy className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-black text-xl tracking-tighter">PodiumOne</span>
        </Link>

        <h1 className="font-display font-bold text-2xl tracking-tight text-center mb-1">
          {mode === "login" ? "Welcome back" : "Create your account"}
        </h1>
        <p className="text-white/50 text-center text-sm mb-6">
          {mode === "login" ? "Sign in to manage your bids" : "Start bidding for the #1 spot"}
        </p>

        <button data-testid="google-login-btn" onClick={googleLogin} className="w-full glass rounded-full h-12 flex items-center justify-center gap-3 hover:border-white/25 transition-colors font-medium mb-5">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
          Continue with Google
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-white/30">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <Label className="text-white/70 text-sm">Name</Label>
              <Input data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5 bg-white/5 border-white/15 focus-visible:ring-blue-500/50" />
            </div>
          )}
          <div>
            <Label className="text-white/70 text-sm">Email</Label>
            <Input data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 bg-white/5 border-white/15 focus-visible:ring-blue-500/50" />
          </div>
          <div>
            <Label className="text-white/70 text-sm">Password</Label>
            <Input data-testid="password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 bg-white/5 border-white/15 focus-visible:ring-blue-500/50" />
          </div>
          <Button data-testid="auth-submit-btn" type="submit" disabled={loading} className="w-full rounded-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:opacity-90 active:scale-95 transition-all font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Sign in" : "Create account"}
          </Button>
        </form>

        <div className="text-center text-sm text-white/50 mt-6">
          {mode === "login" ? "New here? " : "Already have an account? "}
          <button data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-blue-400 hover:text-blue-300 font-medium">
            {mode === "login" ? "Create an account" : "Sign in"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
